import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { getOrCreateSubmission, getSubmission, listTaxForms } from "@/features/forms/api";
import type { FormSubmission } from "@/features/forms/types";
import { Form8379Wizard } from "@/features/workflows/innocent-spouse/components/form-8379-wizard";
import { useActiveClient } from "@/layouts/client-layout";
import { ApiError } from "@/lib/api";

/**
 * Innocent Spouse Analysis workflow (`/client/:id/workflows/innocent-spouse`):
 * resolves (or creates) the client's single Form 8379 submission and renders the
 * 3-tab wizard over it. The same wizard is also reachable from the forms library
 * at `/client/:id/irs-forms/8379` — both are views of this one record. Honors a
 * `?part=1|2|3` deep-link (handled inside the wizard).
 */
export default function ClientInnocentSpouseWorkflowPage() {
  const { client } = useActiveClient();
  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listTaxForms()
      .then((forms) => {
        const taxForm = forms.find((f) => f.formNumber === "8379");
        if (!taxForm) throw new ApiError(404, "Form 8379 is not available.", null);
        return getOrCreateSubmission(client.id, taxForm.id);
      })
      .then((sub) => getSubmission(sub.id))
      .then((sub) => {
        if (mountedRef.current) setSubmission(sub);
      })
      .catch((err) => {
        if (mountedRef.current) {
          setError(err instanceof ApiError ? err.message : "Couldn't open this workflow.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [client.id]);

  useEffect(load, [load]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={`/client/${client.id}/cases`}>
            <ArrowLeft /> Back to Cases
          </Link>
        </Button>
        <h2 className="text-foreground text-2xl font-bold">Innocent Spouse Analysis</h2>
      </div>
      <p className="text-muted-foreground -mt-2 text-sm">
        Guided Form 8379 (Injured Spouse Allocation) questionnaire for {client.name}.
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error || !submission ? (
        <p className="text-destructive py-8 text-sm">{error ?? "Couldn't open this workflow."}</p>
      ) : (
        <Form8379Wizard client={client} submission={submission} onSubmissionChange={setSubmission} />
      )}
    </div>
  );
}
